/**
 * @vuelto-surface
 * mobile: ok — chips scroll horizontal; card = cliente + servicio
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { useQuotesList } from '../hooks/useQuotesList';
import { countBookedSessionsByQuoteIds } from '../infra/quotesRepo';
import type { QuoteListFilter, ServiceQuoteRequest } from '../domain/types';
import { quoteServiceDisplayName } from '../domain/quoteDisplay';
import { ticketColors } from '../../shared/theme/ticketColors';
import { status as statusTokens } from '@vuelto/tokens';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { formatLocalDateTime } from '@shared/vueltoDateTime';

const FILTER_TABS: { key: QuoteListFilter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'unread', label: 'No leídos' },
  { key: 'requested', label: 'Abiertos' },
  { key: 'quoted', label: 'Cotizados' },
  { key: 'accepted', label: 'Aceptados' },
  { key: 'closed', label: 'Cerrados' },
];

const STATUS_LABELS: Record<string, string> = {
  requested: 'Solicitada',
  needs_info: 'Pide más info',
  visit_proposed: 'Evaluación propuesta',
  visit_scheduled: 'Evaluación agendada',
  quoted: 'Cotizada',
  accepted: 'Aceptada',
  scheduled: 'Agendada',
  completed: 'Completada',
  declined: 'Rechazada',
  expired: 'Vencida',
  cancelled: 'Cancelada',
};

const STATUS_COLORS: Record<string, string> = {
  requested: statusTokens.consultationPending,
  needs_info: statusTokens.consultationPending,
  visit_proposed: statusTokens.consultationAnswered,
  visit_scheduled: statusTokens.consultationAnswered,
  quoted: statusTokens.consultationAnswered,
  accepted: statusTokens.consultationAnswered,
  scheduled: statusTokens.consultationAnswered,
  completed: statusTokens.consultationClosed,
  declined: statusTokens.cancelled,
  expired: statusTokens.consultationClosed,
  cancelled: statusTokens.cancelled,
};

function showTurnosMeta(item: ServiceQuoteRequest): boolean {
  return (
    (item.status === 'accepted' || item.status === 'scheduled' || item.status === 'completed') &&
    item.pricing_mode != null &&
    item.estimated_sessions > 0
  );
}

type QuotesScreenProps = {
  refreshRouteName?: string;
  initialFilter?: QuoteListFilter;
};

export function QuotesScreen({
  refreshRouteName = 'Quotes',
  initialFilter = 'all',
}: QuotesScreenProps) {
  const navigation = useNavigation<NativeStackNavigationProp<{ QuoteDetail: { quoteId: string } }>>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const canView = useModulePermission('BranchSettings', 'view');
  const capabilityGate = useCapabilityGate('service_quotes', {
    title: 'Cotizaciones no disponibles',
    subtitle: 'Este rubro no tiene presupuestos.',
  });
  const [filter, setFilter] = useState<QuoteListFilter>(initialFilter);
  const { quotes, loading, error, reload } = useQuotesList(canView ? branchId : null, filter);
  const [bookedByQuote, setBookedByQuote] = useState<Map<string, number>>(() => new Map());
  const skipFilterReload = useRef(true);

  const loadSessionMeta = useCallback(async (items: ServiceQuoteRequest[]) => {
    const ids = items.filter(showTurnosMeta).map((q) => q.id);
    if (ids.length === 0) {
      setBookedByQuote(new Map());
      return;
    }
    try {
      const map = await countBookedSessionsByQuoteIds(ids);
      setBookedByQuote(map);
    } catch (e) {
      console.error('QuotesScreen session meta', e);
      setBookedByQuote(new Map());
    }
  }, []);

  const load = useCallback(async () => {
    await reload();
  }, [reload]);

  useScreenLoad(refreshRouteName, load);

  // Inbox monta QuotesScreen como hijo: el focus de OrdersInbox ya pasó.
  // Recargar al montar para no quedar con lista vacía al cambiar de segmento.
  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (skipFilterReload.current) {
      skipFilterReload.current = false;
      return;
    }
    void reload();
  }, [filter, reload]);

  useEffect(() => {
    void loadSessionMeta(quotes);
  }, [quotes, loadSessionMeta]);

  if (!capabilityGate.allowed) {
    return (
      <ShellContentFrame style={{ flex: 1, backgroundColor: ticketColors.paper }}>
        {capabilityGate.blocked}
      </ShellContentFrame>
    );
  }

  if (!canView) {
    return (
      <ShellContentFrame style={{ flex: 1, backgroundColor: ticketColors.paper }}>
        <ModuleEmptyState
          title="Sin permiso"
          subtitle="No tenés permiso para ver cotizaciones."
          testID="quotes.no-permission"
        />
      </ShellContentFrame>
    );
  }

  const renderItem = ({ item }: { item: ServiceQuoteRequest }) => {
    const isUnread =
      !item.read_by_store_at && (item.status === 'requested' || item.status === 'needs_info');
    const serviceName = quoteServiceDisplayName(item.product_name);
    const notePreview = item.note.trim();
    const turnosMeta = showTurnosMeta(item)
      ? `${bookedByQuote.get(item.id) ?? 0}/${item.estimated_sessions} turnos`
      : null;

    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => navigation.navigate('QuoteDetail', { quoteId: item.id })}
        accessibilityRole="button"
        accessibilityLabel={`${item.customer_name}, ${serviceName}${turnosMeta ? `, ${turnosMeta}` : ''}`}
        testID="quotes.card"
      >
        <View style={styles.cardHeader}>
          <View style={styles.cardMain}>
            <Text style={[styles.customer, isUnread && styles.customerUnread]} numberOfLines={1}>
              {item.customer_name}
            </Text>
            <Text style={styles.service} numberOfLines={1}>
              {serviceName}
            </Text>
          </View>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: STATUS_COLORS[item.status] ?? statusTokens.consultationClosed },
            ]}
          >
            <Text style={styles.statusText}>{STATUS_LABELS[item.status] ?? item.status}</Text>
            {isUnread ? <View style={styles.unreadDot} /> : null}
          </View>
        </View>
        {notePreview ? (
          <Text style={styles.note} numberOfLines={2}>
            {notePreview}
          </Text>
        ) : null}
        <View style={styles.metaRow}>
          {turnosMeta ? <Text style={styles.turnosMeta}>{turnosMeta}</Text> : null}
          <Text style={styles.date}>{formatLocalDateTime(item.updated_at)}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const filterHeader = (
    <View style={styles.filterBar}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.filterScroll}
        contentContainerStyle={styles.filterRow}
      >
        {FILTER_TABS.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.filterTab, filter === tab.key && styles.filterTabActive]}
            onPress={() => setFilter(tab.key)}
            accessibilityRole="button"
            accessibilityState={{ selected: filter === tab.key }}
            testID={`quotes.filter.${tab.key}`}
          >
            <Text style={[styles.filterTabText, filter === tab.key && styles.filterTabTextActive]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  return (
    <ShellContentFrame style={styles.container}>
      <FlatList
        data={quotes}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        ListHeaderComponent={filterHeader}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={() => void reload()} />}
        contentContainerStyle={quotes.length === 0 ? styles.emptyContainer : undefined}
        ListEmptyComponent={
          !loading ? (
            error ? (
              <ModuleEmptyState
                title="No se pudieron cargar las cotizaciones"
                subtitle={error}
                primaryAction={{ label: 'Reintentar', onPress: () => void reload() }}
                testID="quotes.error"
              />
            ) : (
              <ModuleEmptyState
                title="No hay cotizaciones"
                subtitle={
                  filter !== 'all'
                    ? 'Probá con otro filtro.'
                    : 'Cuando un cliente pida una cotización, aparece acá.'
                }
                testID="quotes.empty"
              />
            )
          ) : null
        }
        {...VIRTUALIZED_LIST_PROPS}
      />
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: ticketColors.paper },
  // Same pattern as OnlineOrdersScreen: header-wrapped horizontal ScrollView so chips
  // stay ~32–36px tall and never stretch as flex columns beside the list.
  filterBar: { flexGrow: 0 },
  filterScroll: { flexGrow: 0 },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    paddingRight: 16,
  },
  filterTab: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: ticketColors.paper2,
    flexShrink: 0,
  },
  filterTabActive: { backgroundColor: ticketColors.gold },
  filterTabText: { fontSize: 13, fontWeight: '600', color: ticketColors.ink2 },
  filterTabTextActive: { color: ticketColors.goldInk, fontWeight: '700' },
  card: {
    backgroundColor: ticketColors.card,
    borderRadius: 10,
    padding: 16,
    marginHorizontal: 12,
    marginVertical: 4,
    borderWidth: 1,
    borderColor: ticketColors.border,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 8,
  },
  cardMain: { flex: 1, minWidth: 0 },
  customer: { fontSize: 16, fontWeight: '700', color: ticketColors.ink },
  customerUnread: { fontWeight: '800' },
  service: { fontSize: 14, fontWeight: '600', color: ticketColors.ink2, marginTop: 2 },
  statusBadge: {
    position: 'relative',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },
  statusText: { fontSize: 11, fontWeight: '700', color: '#fff' },
  note: { fontSize: 13, color: ticketColors.inkLight, marginTop: 8, lineHeight: 18 },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 6,
  },
  turnosMeta: { fontSize: 11, fontWeight: '700', color: ticketColors.ink2 },
  date: { fontSize: 11, color: ticketColors.inkLight, marginLeft: 'auto' },
  unreadDot: {
    position: 'absolute',
    top: -3,
    right: -3,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.gold,
    borderWidth: 1.5,
    borderColor: ticketColors.card,
  },
  emptyContainer: { flex: 1, justifyContent: 'center' },
});
